import React, {useMemo, useCallback, useEffect} from 'react';
import {useMutation} from '@apollo/client';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router';
import {
  IconArrowLeft,
  IconClose,
  IconSaveSolid
} from '@flixbus/honeycomb-icons-react';
import {resetDataTableEntries} from '@/Pages/Home/store/slice';
import releaseProductionCostEntryMutation from '../../graphql/releaseProductionCostEntryMutation';
import ProductionCostHeader from '../ProductionCostHeader';
import {ProductionCostHeaderStepOption} from '../ProductionCostHeader/ProductionCostHeaderSteps/ProductionCostHeaderSteps.types';
import ProductionCostFooter from '../ProductionCostFooter';
import {
  backToSecondDataImportScreen,
  resetProductionCostState
} from '@/Pages/ProductionCost/store/slice';
import {CalculationUserJourneyMode} from '@/Pages/ProductionCost/store/types';
import CalculatedProductionCosts from './CalculatedProductionCosts';
import styles from './index.module.scss';
import {ProductionCostCalculationPath} from '@/Pages/ProductionCost/constants/routePaths';

const ProductionCostStepThree = () => {
  const navigate = useNavigate();

  const productionCostEntryId = useSelector(
    ({productionCost}) => productionCost.id
  );
  const calculationUserJourneyMode = useSelector(
    ({productionCost}) => productionCost.calculationUserJourneyMode
  );
  const dispatch = useDispatch();

  // Redirects to the Home page if productionCostId is not provided
  // Happens when user refreshes the page
  useEffect(() => {
    if (!productionCostEntryId) navigate('/');
  }, []); // eslint-disable-line

  const releaseAndResetDataTable = useCallback(() => {
    dispatch(resetProductionCostState());
    dispatch(resetDataTableEntries());
    navigate('/');
  }, [dispatch, navigate]);

  const [releaseProductionCost, {loading}] = useMutation(
    releaseProductionCostEntryMutation,
    {
      variables: {productionCostEntryId},
      onCompleted: releaseAndResetDataTable
    }
  );

  const backButton = useMemo(
    () => ({
      label: 'Back',
      leftSideIcon: IconArrowLeft,
      onClick: () => {
        dispatch(backToSecondDataImportScreen());
        navigate(`../${ProductionCostCalculationPath.steps.dataEntry}`);
      },
      dataActionName: 'Summary Back button'
    }),
    [dispatch, navigate]
  );

  const closeButton = useMemo(
    () => ({
      label: 'Close',
      leftSideIcon: IconClose,
      onClick: releaseAndResetDataTable,
      appearance: 'primary',
      dataActionName: 'Summary Close button'
    }),
    [releaseAndResetDataTable]
  );

  const saveButton = useMemo(
    () => ({
      label: 'Save calculation',
      leftSideIcon: IconSaveSolid,
      onClick: releaseProductionCost,
      appearance: 'primary',
      dataActionName: 'Summary Save button',
      disabled: loading
    }),
    [loading, releaseProductionCost]
  );

  return (
    <>
      <div className={styles.header}>
        <ProductionCostHeader
          mode={calculationUserJourneyMode}
          step={ProductionCostHeaderStepOption.Summary}
        />
      </div>

      <CalculatedProductionCosts />

      <ProductionCostFooter
        leftSideButtons={[backButton]}
        rightSideButtons={
          calculationUserJourneyMode ===
          CalculationUserJourneyMode.EditCalculation
            ? [closeButton]
            : [saveButton]
        }
      />
    </>
  );
};

export default React.memo(ProductionCostStepThree);
